import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Calendar, MapPin, Expand, X, CalendarDays } from "lucide-react";
import { api, API } from "@/lib/site";

export const Events = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);

  useEffect(() => {
    api
      .get("/events")
      .then((r) => setEvents(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (!loading && events.length === 0) return null;

  return (
    <section id="events" data-testid="events-section" className="relative py-24 sm:py-32 px-6 sm:px-10">
      <div className="max-w-6xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-14"
        >
          <p className="text-sm uppercase tracking-[0.3em] text-[#D4AF37] mb-5 flex items-center justify-center gap-2">
            <CalendarDays className="h-4 w-4" /> Upcoming
          </p>
          <h2 className="font-display text-4xl sm:text-6xl font-semibold text-[#F9F6F0]">
            Church <span className="gold-text italic">Events</span>
          </h2>
          <p className="text-[#A19CA8] text-lg mt-6 max-w-2xl mx-auto leading-relaxed">
            Come fellowship with us. Tap any flyer to view the full details.
          </p>
        </motion.div>

        {loading ? (
          <p className="text-center text-[#A19CA8]">Loading events…</p>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {events.map((e, i) => (
              <motion.div
                key={e.id}
                data-testid="event-card"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: i * 0.08 }}
                className="group bg-[#130A1F]/70 border border-[#D4AF37]/25 overflow-hidden hover:border-[#D4AF37]/60 transition-all duration-500"
              >
                <button
                  onClick={() => setActive(e)}
                  data-testid={`event-flyer-${e.id}`}
                  className="relative block w-full overflow-hidden"
                >
                  <img
                    src={`${API}/events/${e.id}/image`}
                    alt={e.title}
                    className="w-full h-auto transition-transform duration-700 group-hover:scale-[1.04]"
                  />
                  <span className="absolute inset-0 bg-[#06040A]/0 group-hover:bg-[#06040A]/25 transition-colors flex items-center justify-center">
                    <span className="opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-2 bg-[#06040A]/80 text-[#D4AF37] px-4 py-2 text-sm uppercase tracking-wider border border-[#D4AF37]/40">
                      <Expand className="h-4 w-4" /> View Flyer
                    </span>
                  </span>
                </button>
                <div className="p-5">
                  <h3 className="font-display text-xl text-[#F9F6F0]">{e.title}</h3>
                  {e.event_date && (
                    <p className="text-[#A19CA8] text-sm mt-2 flex items-center gap-2">
                      <Calendar className="h-4 w-4 text-[#D4AF37] shrink-0" /> {e.event_date}
                    </p>
                  )}
                  {e.location && (
                    <p className="text-[#A19CA8] text-sm mt-1.5 flex items-center gap-2">
                      <MapPin className="h-4 w-4 text-[#D4AF37] shrink-0" /> {e.location}
                    </p>
                  )}
                  {e.description && (
                    <p className="text-[#A19CA8] text-sm mt-3 leading-relaxed line-clamp-4">{e.description}</p>
                  )}
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>

      {active && (
        <div
          className="fixed inset-0 z-[100] bg-[#06040A]/95 backdrop-blur-sm flex items-center justify-center p-4 sm:p-10"
          onClick={() => setActive(null)}
          data-testid="event-modal"
        >
          <button
            onClick={() => setActive(null)}
            data-testid="event-close"
            className="absolute top-6 right-6 text-[#D4AF37] hover:text-[#F3D56D] transition-colors"
            aria-label="Close"
          >
            <X className="h-8 w-8" />
          </button>
          <img
            src={`${API}/events/${active.id}/image`}
            alt={active.title}
            onClick={(ev) => ev.stopPropagation()}
            className="max-h-[90vh] w-auto border border-[#D4AF37]/40 shadow-2xl"
          />
        </div>
      )}
    </section>
  );
};
